import { ISOStringFormat } from "date-fns";
import {
  BillingFrequency,
  ServiceDuration
} from "@/pages/settings/pages/products-v2/types";
import { CsmRenewalConfidenceLevel } from "@/pages/deals/types";
import { SavedAttachmentInfo } from "@/entities/attachments/types";

export interface ContractUploadFiles {
  contracts: SavedAttachmentInfo[];
}

// Account-setting key that gates the contract onboarding phase feature.
export const ENABLE_ONBOARDING_PHASE_KEY = "enable_onboarding_phase";

export enum ContractTab {
  OVERVIEW = "overview",
  TERMS = "termsAndServices",
  ACTIVITIES = "activities",
  PEOPLE = "people",
  ONBOARDING = "onboarding",
  RETENTION = "retentionWorkflows",
  ADDITIONAL_INFO = "additionalInfo"
}

export const isContractTab = (value: string | null): value is ContractTab =>
  !!value && (Object.values(ContractTab) as string[]).includes(value);

export enum ContractStatus {
  ACTIVE = 1,
  INACTIVE = 2,
  EXPIRED = 3,
  CANCELLED = 4
}

export enum EqPaymentsBillingFrequency {
  DAYS = 1,
  WEEKS = 2,
  MONTHS = 3,
  YEARS = 4
}

export interface ScheduledPayment {
  id: string;
  revenueItemId: string;
  paymentNumber: number;
  billingDate: ISOStringFormat;
  amount: number | null;
  percentage: number | null;
  createdAt: ISOStringFormat;
  isDeleted: boolean;
}

export interface ContractRevenueItem {
  id: string;
  contractId: string;
  serviceItemId: string | null;
  serviceItemName: string | null;
  serviceItemPricingId: string | null;
  serviceStartDate: ISOStringFormat | null;
  serviceEndDate: ISOStringFormat | null;
  autoRenewal: boolean | null;
  quantity: number | null;
  pricePerUnit: number | null;
  isDiscountPercentage: boolean | null;
  discountAmount: number | null;
  total: number | null;
  totalLocal: number | null;
  billingStartDate: ISOStringFormat | null;
  billingEndDate: ISOStringFormat | null;
  customBillingFrequency: BillingFrequency | null;
  customServiceDuration: ServiceDuration | null;
  customOptOutDate: string | null;
  customDuration: string | null;
  status: number | null;
  durationDisplay: string | null;
  eqPaymentsNumOfPayments: number | null;
  eqPaymentsBillingFrequency: EqPaymentsBillingFrequency | null;
  createdAt: ISOStringFormat;
  isDeleted: boolean;
  scheduledPayments: ScheduledPayment[];
  itemOrder: number | null;
  // The contract bundle the line belongs to; null on a standalone line.
  contractBundleId?: string | null;
  // Whether the line's dates move with its bundle's services start date.
  followsBundleStartDate?: boolean | null;
  // The line's share of its bundle's discount, already taken off total (account
  // currency) and totalLocal (contract currency); null on a standalone line.
  bundleDiscount?: number | null;
  bundleDiscountLocal?: number | null;
  // Free-text annotation on the service; not part of the terms, never versioned.
  note: string | null;
  // Who last wrote the note (a user id) and when; set by the server.
  noteUpdatedBy: string | null;
  noteUpdatedAt: ISOStringFormat | null;
  // 1 is the oldest elapsed auto-renewal period, ascending forward in time;
  // null on the current-period row.
  periodIndex: number | null;
  // Elapsed auto-renewal periods of this service, most-recent-first. Always an
  // array — the backend returns [] when auto-renewal is off (hidden, not
  // deleted) and repopulates it when it is switched back on. Entries are full
  // revenue items with their own empty previousPeriods; there is no recursion.
  previousPeriods: ContractRevenueItem[];
}

export interface ContractData {
  id: string;
  name: string;
  dealId: string;
  companyId: string;
  productId: string | null;
  status: ContractTableStatus | null;
  arr: number;
  arrLocal: number | null;
  mrr: number;
  mrrLocal: number | null;
  carr: number | null;
  carrLocal: number | null;
  cmrr: number | null;
  cmrrLocal: number | null;
  calculatedCarr: number | null;
  calculatedCarrLocal: number | null;
  calculatedCmrr: number | null;
  calculatedCmrrLocal: number | null;
  calculatedArr: number | null;
  calculatedArrLocal: number | null;
  calculatedMrr: number | null;
  calculatedMrrLocal: number | null;
  // Computed from revenue items at read-time; never overwritten by financial_override
  calculatedAnnualContractValue: number | null;
  calculatedAnnualContractValueLocal: number | null;
  calculatedTotalContractValue: number | null;
  calculatedTotalContractValueLocal: number | null;
  currency: string | null;
  revenueItems: ContractRevenueItem[];
  annualContractValue: number | null;
  annualContractValueLocal: number | null;
  totalContractValue: number | null;
  totalContractValueLocal: number | null;
  financialNote: string | null;
  startDate: ISOStringFormat;
  endDate: ISOStringFormat;
  duration: number;
  renewalType: ContractRenewalType | null;
  nextRenewal: ISOStringFormat | null;
  signedOn: ISOStringFormat | null;
  csmOwner: string | null;
  aeOwner: string | null;
  financialOverride: boolean | null;
  isDetachedFromDeal: boolean | null;
  // Set when this row is the frozen per-deal snapshot created at closed-won;
  // such rows are hidden from the contracts list (pinned DRAFT) and the deal
  // page reaches them via deals.contractId.
  snapshotOfDealId: string | null;
  // Id of the source contract when this row was created by duplication —
  // for snapshots, this is the live contract.
  duplicatedFromContractId: string | null;
  manuallyAddedPeople: string[] | null;
  automaticallyAddedPeople: string[] | null;
  partnerId?: string | null;
  manuallyAddedPartnerContacts?: string[] | null;
  automaticallyAddedPartnerContacts?: string[] | null;
  onboardingStartDate: ISOStringFormat | null;
  onboardingCompletedAt: ISOStringFormat | null;
  onboardingStageId: string | null;
  createdAt: ISOStringFormat;
  csmRenewalConfidenceLevel: CsmRenewalConfidenceLevel | null;
  csmRenewalConfidenceLevelLabel: string | null;
  onboardingStatus: OnboardingStatus | null;
  percentageFromCsmRenewalBook: number | null;
  uploadFiles: ContractUploadFiles | null;
  // FX rates from the contract currency to the account currency: 1 unit of
  // `currency` is worth `rate` units of the account currency. All four are
  // null when the contract is in the account currency. Optional because
  // fixtures and older payloads predate them.
  closeFxRate?: number | null;
  currentFxRate?: number | null;
  currentFxRateRefreshedAt?: ISOStringFormat | null;
  // The user who set the current rate by hand. Null means it is automatic.
  currentFxRateModifiedBy?: string | null;
}

// Backend-provided onboarding lifecycle bucket (accountsession computed field).
// Values are the stable strings the API returns; the KPI maps them to i18n labels.
export enum OnboardingStatus {
  NOT_STARTED = "Not started",
  IN_PROGRESS = "In progress",
  COMPLETED = "Completed"
}

// Mirrors the backend ContractStatusEnum — a value missing here renders as "Unknown".
export enum ContractTableStatus {
  ACTIVE = 1,
  UPCOMING = 2,
  EXPIRED = 3,
  NO_SERVICES = 4,
  ONE_TIME = 5,
  DRAFT = 6
}

export enum ContractRenewalType {
  AUTO = 1,
  MANUAL = 2,
  MIXED = 3
}

export enum ContractColumn {
  NAME = "name",
  COMPANY_NAME = "companyName",
  PRODUCT_NAME = "productName",
  STATUS = "status",
  RENEWAL_TYPE = "renewalType",
  NEXT_RENEWAL = "nextRenewal",
  NEXT_OPT_OUT = "nextOptOut",
  TCV = "totalContractValue",
  ACV = "annualContractValue",
  MRR = "mrr",
  ARR = "arr",
  CMRR = "cmrr",
  CARR = "carr",
  // The terms-currency half of each money metric. Listed here so the built-in
  // column owns the id and the registry field of the same key is not mapped a
  // second time as a custom column.
  TCV_TERMS = "totalContractValueLocal",
  ACV_TERMS = "annualContractValueLocal",
  MRR_TERMS = "mrrLocal",
  ARR_TERMS = "arrLocal",
  CMRR_TERMS = "cmrrLocal",
  CARR_TERMS = "carrLocal",
  SERVICE_COUNT = "serviceCount",
  PERIOD = "period",
  DURATION = "duration",
  SIGNED_ON = "signedOn",
  CURRENCY = "currency",
  // Named as the API fields, so the filter and sort keys need no mapping.
  CLOSE_FX_RATE = "closeFxRate",
  CURRENT_FX_RATE = "currentFxRate",
  CSM_OWNER = "csmOwner",
  AE_OWNER = "aeOwner",
  SOURCE_DEAL = "dealName",
  ONBOARDING_STAGE = "onboardingStageName",
  CSM_RENEWAL_CONFIDENCE = "csmRenewalConfidenceLevel"
}

export interface ContractTableRow {
  id: string;
  name: string;
  companyId: string | null;
  companyName: string | null;
  productId: string | null;
  productName: string | null;
  status: ContractTableStatus | null;
  statusResolved: string | null;
  renewalType: ContractRenewalType | null;
  renewalTypeResolved: string | null;
  nextRenewal: ISOStringFormat | null;
  nextOptOut: ISOStringFormat | null;
  totalContractValue: number | null;
  totalContractValueLocal: number | null;
  annualContractValue: number | null;
  annualContractValueLocal: number | null;
  mrr: number | null;
  mrrLocal: number | null;
  arr: number | null;
  arrLocal: number | null;
  cmrr: number | null;
  cmrrLocal: number | null;
  carr: number | null;
  carrLocal: number | null;
  serviceCount: number | null;
  startDate: ISOStringFormat | null;
  endDate: ISOStringFormat | null;
  duration: number | null;
  durationDisplay: string | null;
  signedOn: ISOStringFormat | null;
  currency: string | null;
  csmOwner: string | null;
  csmOwnerName: string | null;
  aeOwner: string | null;
  aeOwnerName: string | null;
  dealId: string | null;
  dealName: string | null;
  onboardingStartDate: string | null;
  onboardingCompletedAt: string | null;
  onboardingStageId: string | null;
  onboardingStageName: string | null;
  onboardingStageColor: string | null;
  csmRenewalConfidenceLevel: CsmRenewalConfidenceLevel | null;
  csmRenewalConfidenceLevelLabel: string | null;
  // Same meaning as on ContractData.
  closeFxRate?: number | null;
  currentFxRate?: number | null;
  currentFxRateRefreshedAt?: ISOStringFormat | null;
  currentFxRateModifiedBy?: string | null;
}
